import { React, useState, useEffect, useContext } from "react";
import httpInjectorService from "../../services/http-injector.service";
import { useParams } from "react-router";
import { Table, Spin, Card, Input, Button, Space, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { NotificationContext } from "../../NotificationProvider";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useLocation } from "react-router";
import dayjs from "dayjs";
import { Col, Row } from "reactstrap";

const PaysheetDetails = () => {
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingKey, setEditingKey] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const { id } = useParams();
  const { openNotification } = useContext(NotificationContext);
  const location = useLocation();
  const { filename } = location.state || {};
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);

  const getPaysheetData = async () => {
    setLoadingData(true);
    try {
      const response = await httpInjectorService.getPaysheetData(id);
      if (response.status === "SUCCESS" && response.data.length > 0) {
        const dataWithKeys = response.data.map((item, index) => ({
          ...item,
          key: index,
        }));
        setTableData(dataWithKeys);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching paysheet data:", error);
      setTableData([]);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    getPaysheetData();
  }, []);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
  };

  // const handleEdit = (record) => {
  //   setEditingKey(record.key);
  //   setEditedRow({ ...record });
  // };
  const handleEdit = (record) => {
    const formattedRecord = {
      ...record,
      pay_date: formatDisplayDate(record.pay_date),
    };
    setEditingKey(record.key);
    setEditedRow(formattedRecord);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditedRow({});
  };
  const formatDisplayDate = (dateStr) => {
    return dayjs(dateStr).isValid()
      ? dayjs(dateStr).format("DD-MM-YYYY")
      : dateStr;
  };

  const formatSaveDate = (dateStr) => {
    // Assumes incoming string is in DD_MM_YY format
    const parsed = dayjs(dateStr, "DD-MM-YYYY");
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : dateStr;
  };

  const handleSave = async (key) => {
    const originalRow = tableData.find((item) => item.key === key);
    const updatedRow = { ...originalRow, ...editedRow };

    const updatedFields = {};
    Object.keys(editedRow).forEach((field) => {
      let value = editedRow[field];
      if (field === "pay_date") {
        value = formatSaveDate(value); // Convert to YYYY-MM-DD
      }

      if (value !== originalRow[field]) {
        updatedFields[field] = value;
        updatedRow[field] = value;
      }
    });

    if (Object.keys(updatedFields).length === 0) {
      setEditingKey(null);
      setEditedRow({});
      return;
    }

    try {
      const response = await httpInjectorService.updatePaysheet({
        id: originalRow.id,
        file_id: originalRow.file_id,
        updated_fields: updatedFields,
      });

      const updatedData = tableData.map((item) =>
        item.key === key ? updatedRow : item
      );

      if (response.status === "SUCCESS") {
        setTableData(updatedData);
        openNotification("success", response.message);
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error("Error updating paysheet:", error);
    } finally {
      setEditingKey(null);
      setEditedRow({});
    }
  };

  const filteredTableData = tableData.filter((row) => {
    const values = Object.values(row).join(" ").toLowerCase();
    return values.includes(searchTerm.toLowerCase());
  });

  const renderEditableCell = (dataIndex) => (text, record) => {
    const value =
      dataIndex === "pay_date"
        ? formatDisplayDate(record[dataIndex])
        : record[dataIndex];

    return editingKey === record.key ? (
      <Input
        value={editedRow[dataIndex]}
        onChange={(e) =>
          setEditedRow({ ...editedRow, [dataIndex]: e.target.value })
        }
      />
    ) : (
      value
    );
  };

  const columns = [
    {
      title: "SL.NO",
      key: "slno",
      fixed: "left",
      render: (_text, _record, index) =>
        (currentPage - 1) * pageSize + index + 1,
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "FIRST NAME",
      dataIndex: "first_name",
      key: "first_name",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "LAST NAME",
      dataIndex: "last_name",
      key: "last_name",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "WORK NUMBER",
      dataIndex: "work_number",
      key: "work_number",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "TAX CODE",
      dataIndex: "tax_code",
      key: "tax_code",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "PAY PERIOD",
      dataIndex: "pay_period",
      key: "pay_period",
      render: renderEditableCell("pay_period"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "PAY DATE",
      dataIndex: "pay_date",
      key: "pay_date",
      render: renderEditableCell("pay_date"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "PAY TYPE",
      dataIndex: "pay_type",
      key: "pay_type",
      render: renderEditableCell("pay_type"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "NI NUMBER",
      dataIndex: "ni_number",
      key: "ni_number",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "PAYMENT SUMMARY",
      dataIndex: "payment_summary",
      key: "payment_summary",
      render: renderEditableCell("payment_summary"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "TOTAL PAYMENT",
      dataIndex: "total_payments",
      key: "total_payments",
      render: renderEditableCell("total_payments"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "NET PAY",
      dataIndex: "net_pay",
      key: "net_pay",
      render: renderEditableCell("net_pay"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
    {
      title: "ACTION",
      key: "action",
      fixed: "right",
      render: (_text, record) => {
        const isEditing = editingKey === record.key;
        return isEditing ? (
          <Space>
            <Button onClick={() => handleSave(record.key)}>Save</Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </Space>
        ) : (
          <Button
            style={{ color: "#5167c1" }}
            onClick={() => handleEdit(record)}
          >
            <EditOutlined />
          </Button>
        );
      },
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
    },
  ];

  const handleDownloadExcel = async () => {
    if (!tableData || tableData.length === 0) {
      message.warning("No data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Paysheet");

    // Fields to exclude
    const excludedFields = ["id", "file_id", "key"];

    // Get headers, excluding unwanted fields
    const headers = Object.keys(tableData[0]).filter(
      (key) => !excludedFields.includes(key.toLowerCase())
    );

    worksheet.columns = headers.map((key) => ({
      header: key.replace(/_/g, " ").toUpperCase(),
      key: key,
      width: 20,
    }));

    tableData.forEach((row) => {
      const filteredRow = {};
      headers.forEach((key) => {
        filteredRow[key] = row[key];
      });
      worksheet.addRow(filteredRow);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `${filename}`;
    saveAs(blob, fileName);
  };
  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Paysheet Details</span>
            <Button
              key="download"
              style={{ backgroundColor: "#5167c1", color: "white" }}
              onClick={handleDownloadExcel}
            >
              Download
            </Button>
          </div>
        }
        bordered={true}
        style={{ minHeight: "600px" }}
      >
        <Row
          className="align-items-center mb-3"
          style={{ gap: 16, flexWrap: "wrap" }}
        >
          <Col className="col-sm-2">
            <select
              className="form-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </Col>

          <Col className="col-sm-3">
            <div className="position-relative">
              <i
                className="bx bx-search-alt"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "18px",
                  color: "#aaa",
                }}
              ></i>
              <input
                onChange={(e) => setSearchTerm(e.target.value)}
                id="search-bar-0"
                type="text"
                className="form-control ps-5"
                placeholder={`${tableData.length} record${
                  tableData.length !== 1 ? "s" : ""
                }...`}
                value={searchTerm || ""}
                style={{ height: "38px" }}
              />
            </div>
          </Col>
        </Row>
        <Spin spinning={loadingData} tip="Loading data...">
          <Table
            columns={columns}
            dataSource={filteredTableData}
            rowKey="key"
            pagination={
              filteredTableData.length > pageSize ? { pageSize } : false
            }
            bordered
            scroll={{ x: "max-content" }}
            onChange={handleTableChange}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default PaysheetDetails;
